import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Clock, DollarSign, MapPin, Route } from "lucide-react"; // Assuming you have lucide-react

// Styled components for the vehicle selection UI
const VehicleSelectionContainer = styled(motion.div)`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const TripInfoCard = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #1e40af;
  }

  .info-content {
    display: flex;
    flex-direction: column;

    .label {
      font-size: 0.875rem;
      color: #64748b;
    }

    .value {
      font-weight: 600;
      color: #1e293b;
    }
  }
`;

const SectionTitle = styled.h3`
  color: #1e40af;
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  position: relative;
  padding-bottom: 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 3rem;
    height: 3px;
    background: #1e40af;
    border-radius: 3px;
  }
`;

const VehicleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VehicleCard = styled(motion.div)`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 2px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: #1e40af;
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  
  .vehicle-image {
    width: 100%;
    height: 140px;
    object-fit: cover;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    background: #f1f5f9;
  }
  
  .vehicle-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 1rem 0;
  }
  
  .vehicle-details {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .price {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #1e40af;
    font-weight: 700;
    font-size: 1.25rem;
    margin-top: 1rem;
    
    svg {
      color: #1e40af;
    }
  }
  
  .highlight {
    position: absolute;
    top: 1rem;
    right: -2rem;
    background: #eab308;
    color: white;
    padding: 0.25rem 2rem;
    transform: rotate(45deg);
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: ${props => props.isRecommended ? 'block' : 'none'};
  }
`;

const VehicleDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #64748b;
  }
  
  span {
    color: #64748b;
    font-size: 0.875rem;
  }
`;

const BackButton = styled(motion.button)`
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: #e2e8f0;
  }
`;

// Main component
const VehicleSelection = ({ vehicles, onVehicleSelect, onBack }) => {
  // No recommended vehicle - removed recommendation logic
  const recommendedVehicle = null;
  
  return (
    <VehicleSelectionContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <BackButton 
        onClick={onBack}
        whileTap={{ scale: 0.97 }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to booking
      </BackButton>
      
      <TripInfoCard
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <InfoItem>
          <Route size={24} />
          <div className="info-content">
            <span className="label">Distance</span>
            <span className="value">{vehicles[0]?.distance}</span>
          </div>
        </InfoItem>
        
        <InfoItem>
          <Clock size={24} />
          <div className="info-content">
            <span className="label">Estimated time</span>
            <span className="value">{vehicles[0]?.estimatedTime}</span>
          </div>
        </InfoItem>
        
        <InfoItem>
          <MapPin size={24} />
          <div className="info-content">
            <span className="label">Your ride is ready</span>
            <span className="value">Select a vehicle type</span>
          </div>
        </InfoItem>
      </TripInfoCard>
      
      <SectionTitle>Choose your vehicle</SectionTitle>
      
      <VehicleGrid>
        {vehicles.map((vehicle, index) => (
          <VehicleCard
            key={vehicle.id}
            isRecommended={vehicle.id === recommendedVehicle?.id}
            onClick={() => onVehicleSelect(vehicle.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {vehicle.id === recommendedVehicle?.id && (
              <div className="highlight">Recommended</div>
            )}
            
            <img 
              src={vehicle.imageUrl} 
              alt={vehicle.typeName} 
              className="vehicle-image"
            />
            
            <h4 className="vehicle-name">{vehicle.typeName}</h4>
            
            <div className="vehicle-details">
              <VehicleDetail>
                <Clock size={16} />
                <span>ETA: {vehicle.estimatedTime}</span>
              </VehicleDetail>
              
              <VehicleDetail>
                <Route size={16} />
                <span>Distance: {vehicle.distance}</span>
              </VehicleDetail>
            </div>
            
            <div className="price">
              <DollarSign size={20} />
              <span>{vehicle.estimatedPrice} ETB</span>
            </div>
          </VehicleCard>
        ))}
      </VehicleGrid>
    </VehicleSelectionContainer>
  );
};

export default VehicleSelection;